import { useState, useEffect, useCallback } from 'react';
import { Modal } from '@/components/utils/Modal';
import type { ConversationCard } from '@/types/dashboard';
import { dashboardGroupService } from '@/services/dashboardGroupService';

import './ConvertToModal.css';
import ConversationApi from '../api/ConversationApi';
import { conversationService } from '@services/conversationService';
import { socketService } from '@/services/socketService';

type ConvertToModalProps = {
  open: boolean;
  setOpen: any;
  conversations?: ConversationCard[];
  existsInvalidItems: boolean;
  api: ApiConfig;
  onConverToProccess: (response: ConversionResult[]) => void;
};

export interface ApiConfig {
  url: string;
  token: string;
  PersonId: string;
  TenancyName: string;
  refreshToken?: string;
  SocketServer?: string;
}

export interface ConversationObj {
  ConversationId: number;
  IsParent?: boolean;
  ParentConversationId?: number;
  DashboardGroupId?: number;
  ConfigValueId: number;
  ProjectId?: number;
  Title?: string;
  Icon?: string;
}

export interface MenuItem {
  ConfigValueID: number;
  Text?: string;
  Name?: string;
  Icon?: string;
  selected?: boolean;
}

export interface ConversionResult {
  ConversationId: number;
  WasConverted: boolean;
  WarningCode?: number;
  ErrorCode?: number;
}

interface ConvertedError {
  conversations: ConversationObj[];
}

const ConvertToModal = ({
  open,
  setOpen,
  conversations,
  existsInvalidItems,
  api,
  onConverToProccess,
}: ConvertToModalProps) =>
{
  // const token = authStorage.getAccessToken() || '';
  // const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost/Corgee.Web/';
  // const socketServer =
  //     import.meta.env.VITE_SOCKET_SERVER || 'http://localhost:8880/usersNotificationManager';
  // const tenancyName = import.meta.env.VITE_TENANCY_NAME || 'cgbot';
  const BASE_WARNING =
    'Warning: You could lose some data fields, the fields will be mapped by name.';

  // Derive "first" conversation (parent or first in list)
  const firstConversation = (() =>
  {
    if (!conversations?.length) return null;
    const parent = conversations.find(x => x.IsParent);
    return parent ?? conversations[0];
  })();

  const modifyHierarchies = firstConversation?.IsParent ?? false;

  // const [api, setApi] = useState<ApiConfig>(() => CorgeeStorageHelper.getApiConfig());
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [warning, setWarning] = useState<string>(BASE_WARNING);
  const [convertLabel, setConvertLabel] = useState('Convert');
  const [userConfirm, setUserConfirm] = useState(false);
  const [hierarchies, setHierarchies] = useState(false);
  const [showErrWar, setShowErrWar] = useState(false);
  const [errors, setErrors] = useState<Record<string, ConvertedError>>({});
  const [warnings, setWarnings] = useState<Record<string, ConvertedError>>({});
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // useEffect(() => {
  //     setApi(CorgeeStorageHelper.getApiConfig());
  // }, []);

  useEffect(() =>
  {
    if (!conversations?.length || !firstConversation) return;

    let initialWarning = BASE_WARNING;
    if (existsInvalidItems) initialWarning += ' <br/>Only your own tasks will be converted!';
    if (!firstConversation.DashboardGroupId && modifyHierarchies)
      initialWarning +=
        ' <br/><br/>By converting this card, the hierarchy on dashboards will be eliminated. In order to keep the hierarchy, convert from the dashboard.';
    setWarning(initialWarning);

    const ids = conversations?.map(o => o.ConversationId).join(',') ?? '';

    dashboardGroupService
      .GetItemTypeToConvertBySecurity(0, 0, ids)
      .then((response: any) =>
      {
        const filtered = response.filter(
          (e: any) =>
            e.ConfigValueID !== 1 &&
            e.ConfigValueID !== 9 &&
            e.ConfigValueID !== 15 &&
            e.ConfigValueID !== firstConversation?.ConfigValueId,
        );
        setMenu(filtered.map((m: any) => ({ ...m, selected: false })));
      })
      .catch(() => console.error('Error getting menu data'))
      .finally(() => setLoading(false));
  }, []);

  const resetMenuSelection = useCallback(() =>
  {
    setMenu(prev => prev.map(m => ({ ...m, selected: false })));
    setUserConfirm(false);
  }, []);

  const selectMenu = (item: MenuItem) =>
  {
    let w = BASE_WARNING;
    if (existsInvalidItems) w += ' <br/>Only your own tasks will be converted!';
    setWarning(w);
    setConvertLabel('Convert');
    resetMenuSelection();
    setMenu(prev =>
      prev.map(m =>
        m.ConfigValueID === item.ConfigValueID ? { ...m, selected: !item.selected } : m,
      ),
    );
  };

  const selectedItem = menu.find(m => m.selected) ?? null;

  const getIds = () => conversations?.map(o => o.ConversationId.toString()).join(',') ?? '';

  const processResponse = async (response: ConversionResult[]) =>
  {
    // API may return a single object instead of array — normalize it
    const responseArr: ConversionResult[] = Array.isArray(response)
      ? response
      : response
        ? [response as any]
        : [];
    const errList = responseArr.filter(r => !r.WasConverted);
    const warnList = responseArr.filter(r => r.WarningCode);

    if (errList.length || warnList.length)
    {
      const wTemp: ConversationObj[] = [];
      const eTemp: ConversationObj[] = [];

      for (const w of warnList)
      {
        const requestParams = {
          layersToHide: '',
          getAllReplies: false,
          isConversationDetailsView: true,
          totalReplies: 0,
        };
        const conv = await conversationService.getConversationDetails(
          w.ConversationId,
          requestParams,
        );
        setShowErrWar(true);
        wTemp.push(conv as any);
        setWarnings(prev => ({
          ...prev,
          [`w${w.WarningCode}`]: { conversations: [...wTemp] },
        }));
        (conversationService.deleteConversation as any)(conv.ConversationId);
      }

      for (const e of errList)
      {
        const conv = await conversationService.getConversationDetails(e.ConversationId, {} as any);
        setShowErrWar(true);
        eTemp.push(conv as any);
        setErrors(prev => ({
          ...prev,
          [`e${e.ErrorCode}`]: { conversations: [...eTemp] },
        }));
      }
      //handlerBindedFns.ononConverToProccess(response);
    }
    else
    {
      // Use ALL submitted conversations (not just API response) so every
      // selected card gets its icon/type refreshed visually.
      const allResults: ConversionResult[] = (conversations ?? []).map(c => ({
        ConversationId: c.ConversationId,
        WasConverted: true,
      }));
      onConverToProccess(allResults.length > 0 ? allResults : responseArr);
      setOpen();
    }
  };

  const confirm = async () =>
  {
    if (!selectedItem || !firstConversation)
    {
      setWarning('First select one option.');
      return;
    }
    if (isProcessing) return;

    setIsProcessing(true);
    setShowErrWar(false);
    const ids = getIds();
    let dashboardGroupId = 0;
    let localUserConfirm = userConfirm;

    if (hierarchies)
    {
      dashboardGroupId = firstConversation.DashboardGroupId ?? 0;
      localUserConfirm = true;
    }

    const doConvert = async () =>
    {
      const response = await dashboardGroupService.ConvertConversation(
        selectedItem.ConfigValueID,
        ids,
        hierarchies,
        dashboardGroupId,
        firstConversation.ProjectId || 0,
        socketService.connectionId || '',
      );
      await ConversationApi.SetConversationsAsViewed(api as any, { ConversationIds: ids });
      await processResponse(response as any);
    };

    try
    {
      if (!localUserConfirm)
      {
        const data = await dashboardGroupService.ValidateConversationToConvert(
          firstConversation.ConversationId,
          selectedItem.ConfigValueID,
        );

        if (data.TypeErrorId === 0)
        {
          await doConvert();
        }
        else if (data.TypeErrorId === 1)
        {
          setWarning(data.Message);
        }
        else if (data.TypeErrorId === 2)
        {
          setConvertLabel('Continue!');
          setWarning(data.Message);
          setUserConfirm(true);
        }
      }
      else
      {
        await doConvert();
      }
    }
    finally
    {
      setIsProcessing(false);
    }
  };

  const showHierarchiesCheckbox = modifyHierarchies && !!firstConversation?.DashboardGroupId;

  return (
    <Modal open={open} onClose={setOpen} title={`Convert To`} preventCloseClickOut>
      {/* ── Content ── */}
      <div
        className="min-h-44 mb-4 w-[423px] text-[#414042] dark:text-[#a9a093]"
        style={{ padding: 0, marginTop: 10 }}
      >
        {/* Normal view */}
        {!showErrWar && (
          <div className="overflow-x-hidden">
            {/* Menu items */}
            {loading ? (
              <div className="cto-loading">Loading options…</div>
            ) : (
              <div className="icons-options-convert !overflow-x-auto">
                {menu.map(item => (
                  <div
                    key={item.ConfigValueID}
                    className={`flex flex-row items-center gap-4 listMenuItemType${item.selected ? ' listMenuItemType-selected' : ''}`}
                    onClick={() => selectMenu(item)}
                  >
                    <div className="cto-icon-col">
                      <div className={item.Icon ?? ''} />
                    </div>
                    <div className="cto-text-col">
                      <span>{item.Text ?? item.Name}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Warning message */}
            <div className="convert-warning-style" dangerouslySetInnerHTML={{ __html: warning }} />

            {/* Hierarchy checkbox */}
            {showHierarchiesCheckbox && (
              <div
                className="sm:col-span-12 md:col-span-12 flex justify-end text-left text-xs items-center flex"
                style={{ paddingLeft: 0 }}
              >
                <div style={{ paddingLeft: 0, paddingRight: 0, marginLeft: 3 }}>
                  <input
                    type="checkbox"
                    checked={hierarchies}
                    onChange={e => setHierarchies(e.target.checked)}
                  />
                  <span className="dark:text-[#a9a093]">
                    {' '}
                    Keep this hierarchy and Convert all members.
                  </span>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="col-sm-12 col-md-12 right text-right" style={{ padding: 0 }}>
              <div style={{ padding: 0 }}>
                <input
                  className="react-corgee-wizard_cancel-btn btn btn-default dark:bg-[#5c6060] dark:hover:brightness-[0.85]"
                  style={{ marginRight: 7 }}
                  type="button"
                  value="Cancel"
                  onClick={setOpen}
                />
                <input
                  className="react-corgee-wizard_corgee-btn btn btn-default dark:bg-[#3c6c80] dark:hover:brightness-[0.85]"
                  type="button"
                  value={isProcessing ? 'Processing…' : convertLabel}
                  disabled={isProcessing}
                  onClick={confirm}
                />
              </div>
            </div>
          </div>
        )}

        {/* Error / Warning results view */}
        {showErrWar && (
          <div>
            {/* Warnings */}
            {Object.entries(warnings).map(([key, war]) => (
              <div key={key} className="error-item text-xs">
                <div className="convert-warning">
                  The following items were converted successfully but there were some errors.
                </div>
                {war.conversations.map(conversation => (
                  <div
                    key={conversation.ConversationId}
                    className="error-item-title items-center flex"
                  >
                    <span className={conversation.Icon ?? ''} />
                    <span> {conversation.Title} </span>
                  </div>
                ))}
              </div>
            ))}

            {/* Errors */}
            {Object.entries(errors).map(([key, err]) => (
              <div key={key} className="error-item text-xs">
                <div className="convert-errors">The following items were not converted.</div>
                {err.conversations.map(conversation => (
                  <div
                    key={conversation.ConversationId}
                    className="error-item-title items-center flex"
                  >
                    <span className={conversation.Icon ?? ''} />
                    <span> {conversation.Title} </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ConvertToModal;
